import moment from 'moment/moment'
import { useEffect, useState } from 'react'

import { EuiBadge, EuiButton, EuiCheckbox, EuiColorPicker, EuiDatePicker, EuiFieldNumber, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiRadioGroup, EuiSpacer, EuiText, EuiTextArea } from '@elastic/eui'

import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'
import { SegmentsForm } from 'components/targeting/TargetingForm/SegmentsForm'
import { CampaignType, getCampaignTypeNameLong } from 'utils/CampaignType'

import { IntervalUnit, LocationPostalCode, PostalCodeTarget, SegmentTarget } from '../../../api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from '../../../api/interfaces/MasterCampaignStub'
import { OffAdCritterCustomOption } from '../../../api/interfaces/Organization'
import { AcTargeterSelection } from '../../../components/targeting/finders/AcTargeterSelection'
import { PostalCodeFinder } from '../../../components/targeting/finders/PostalCodeFinder'
import { offAdCritterCustomColorOptions, offAdCritterPlatformColors, getAvailableColors, getNextAvailableColor } from './GanttChart/utils'

export enum AddCampaignSectionStep {
  CampaignType = 'CampaignType',
  CampaignDetails = 'CampaignDetails'
}

export interface CampaignFormProps {
  campaignBuilder: CampaignFormFields
  setCampaignBuilder: (campaignBuilder: CampaignFormFields) => void
  step: AddCampaignSectionStep
  offAdCritterCustomOptions: OffAdCritterCustomOption[]
}

export interface CampaignFormFields {
  name: string
  shortSummary: string
  longSummary: string
  audienceSummary: string
  geoSummary: string
  seasonalitySummary: string
  campaignType: MasterCampaignPlanCampaignBuilderType
  isOffAdCritter?: boolean
  offAdCritterName?: string
  offAdCritterColor?: string
  intervalBudget?: number
  retargetSite: boolean
  startDate: moment.Moment
  intervalUnit: IntervalUnit
  intervalFrequency: number
  intervalDuration: number
  segmentTargets: SegmentTarget[]
  zips: PostalCodeTarget[]
  cpm?: number
  marginOfError?: number
  includeInProjections?: boolean
  includeInGeoHeatmap?: boolean
  keywords?: string[]
  keywordDescription?: string
  cpc?: number
}

export const CampaignForm = ({ campaignBuilder, setCampaignBuilder, step, offAdCritterCustomOptions }: CampaignFormProps) => {
  const [budgetTouched, setBudgetTouched] = useState(false)
  const [showCustomOffAdCritterInputs, setShowCustomOffAdCritterInputs] = useState(false)
  const [includeInProjections, setIncludeInProjections] = useState(campaignBuilder.includeInProjections)
  const [includeSummary, setIncludeSummary] = useState(!!campaignBuilder.longSummary)
  const [includeTimingDescription, setIncludeTimingDescription] = useState(!!campaignBuilder.seasonalitySummary)
  const [includeGeographyDescription, setIncludeGeographyDescription] = useState(!!campaignBuilder.geoSummary)
  const [includeZipCodes, setIncludeZipCodes] = useState(!!campaignBuilder.zips?.length)
  const [includeAudienceDescription, setIncludeAudienceDescription] = useState(!!campaignBuilder.audienceSummary)
  const [includeInGeoHeatmap, setIncludeInGeoHeatmap] = useState(campaignBuilder.includeInGeoHeatmap)
  const [includeKeywords, setIncludeKeywords] = useState(!!(campaignBuilder.keywordDescription || campaignBuilder.keywords?.length))
  const [keywordInput, setKeywordInput] = useState('')

  const isGoogleCampaign = campaignBuilder.isOffAdCritter && campaignBuilder.offAdCritterName === 'Google'

  const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color) || color.startsWith('linear-gradient') || color.startsWith('radial-gradient')
  }

  const standardPlatforms = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok']
  const sortedCustomOptions = [...offAdCritterCustomOptions].sort((a, b) => a.order - b.order)
  const offAdCritterOptions = [...standardPlatforms.map(name => ({ id: name, label: name })), ...sortedCustomOptions.map(option => ({ id: option.name, label: option.name })), { id: '__custom__', label: 'Custom' }]

  const handleOffAdCritterSelection = (id: string) => {
    if (id === '__custom__') {
      const usedColors = offAdCritterCustomOptions.map(option => option.color)
      const availableColors = getAvailableColors(offAdCritterCustomColorOptions, usedColors)
      const nextColor = getNextAvailableColor(availableColors)

      setShowCustomOffAdCritterInputs(true)
      setCampaignBuilder({
        ...campaignBuilder,
        campaignType: MasterCampaignPlanCampaignBuilderType.OffAdCritter,
        isOffAdCritter: true,
        offAdCritterName: '',
        offAdCritterColor: nextColor
      })
    } else {
      setShowCustomOffAdCritterInputs(false)
      const existingOption = offAdCritterCustomOptions.find(option => option.name === id)
      const color = existingOption?.color || offAdCritterPlatformColors[id] || '#999999'
      setCampaignBuilder({
        ...campaignBuilder,
        campaignType: MasterCampaignPlanCampaignBuilderType.OffAdCritter,
        isOffAdCritter: true,
        offAdCritterName: id,
        offAdCritterColor: color
      })
    }
  }

  useEffect(() => {
    if ((!includeZipCodes || (campaignBuilder.zips?.length ?? 0) === 0) && includeInGeoHeatmap) {
      setIncludeInGeoHeatmap(false)
    }
  }, [includeZipCodes, campaignBuilder.zips?.length, includeInGeoHeatmap])

  return (
    <>
      {step === AddCampaignSectionStep.CampaignType && (
        <>
          <EuiFormRow label='Select Campaign Type *'>
            <EuiRadioGroup
              options={[
                { id: MasterCampaignPlanCampaignBuilderType.TV, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.TV) },
                { id: MasterCampaignPlanCampaignBuilderType.Billboard, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Billboard) },
                { id: MasterCampaignPlanCampaignBuilderType.Audio, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Audio) },
                { id: MasterCampaignPlanCampaignBuilderType.Internet, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Internet) }
              ]}
              idSelected={campaignBuilder.isOffAdCritter ? '' : campaignBuilder.campaignType}
              onChange={id => setCampaignBuilder({ ...campaignBuilder, campaignType: id as MasterCampaignPlanCampaignBuilderType, isOffAdCritter: false, offAdCritterName: undefined, offAdCritterColor: undefined })}
              name='campaign type group'
            />
          </EuiFormRow>

          <EuiSpacer size='m' />

          <EuiText size='s' style={{ paddingLeft: '24px' }}>
            Off AdCritter
          </EuiText>
          <EuiRadioGroup style={{ paddingLeft: '36px' }} options={offAdCritterOptions} idSelected={campaignBuilder.isOffAdCritter ? (showCustomOffAdCritterInputs ? '__custom__' : campaignBuilder.offAdCritterName || '') : ''} onChange={handleOffAdCritterSelection} name='off AdCritter group' />

          {showCustomOffAdCritterInputs && (
            <div style={{ paddingLeft: '60px' }}>
              <EuiSpacer size='s' />
              <AcEuiFormRow label='Custom Channel Name *' isInvalid={!campaignBuilder.offAdCritterName}>
                <EuiFieldText value={campaignBuilder.offAdCritterName || ''} onChange={e => setCampaignBuilder({ ...campaignBuilder, offAdCritterName: e.target.value })} />
              </AcEuiFormRow>
              <EuiFormRow label='Color *' isInvalid={!campaignBuilder.offAdCritterColor || !isValidHexColor(campaignBuilder.offAdCritterColor)} error={['Valid hex color is required (e.g., #999999)']}>
                <EuiColorPicker
                  color={campaignBuilder.offAdCritterColor || '#999999'}
                  onChange={color => setCampaignBuilder({ ...campaignBuilder, offAdCritterColor: color })}
                  swatches={getAvailableColors(
                    offAdCritterCustomColorOptions,
                    offAdCritterCustomOptions.map(option => option.color)
                  )}
                />
              </EuiFormRow>
            </div>
          )}
        </>
      )}
      {step === AddCampaignSectionStep.CampaignDetails && (
        <>
          <AcEuiFormRow fullWidth label='Campaign Name *' isInvalid={campaignBuilder.name === ''}>
            <EuiFieldText fullWidth value={campaignBuilder.name} onChange={e => setCampaignBuilder({ ...campaignBuilder, name: e.target.value })} />
          </AcEuiFormRow>

          {campaignBuilder.isOffAdCritter ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeSummary' label='Summary' checked={includeSummary} onChange={e => setIncludeSummary(e.target.checked)} />
              </EuiFormRow>

              {includeSummary && (
                <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                  <EuiTextArea fullWidth value={campaignBuilder.longSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, longSummary: e.target.value })} />
                </EuiFormRow>
              )}
            </>
          ) : (
            <EuiFormRow fullWidth label='Summary'>
              <EuiTextArea fullWidth value={campaignBuilder.longSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, longSummary: e.target.value })} />
            </EuiFormRow>
          )}

          {campaignBuilder.isOffAdCritter ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeTimingDescription' label='Timing Description' checked={includeTimingDescription} onChange={e => setIncludeTimingDescription(e.target.checked)} />
              </EuiFormRow>

              {includeTimingDescription && (
                <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                  <EuiTextArea fullWidth value={campaignBuilder.seasonalitySummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, seasonalitySummary: e.target.value })} />
                </EuiFormRow>
              )}
            </>
          ) : (
            <EuiFormRow fullWidth label='Timing Description'>
              <EuiTextArea fullWidth value={campaignBuilder.seasonalitySummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, seasonalitySummary: e.target.value })} />
            </EuiFormRow>
          )}

          {campaignBuilder.isOffAdCritter ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeGeographyDescription' label='Geography Description' checked={includeGeographyDescription} onChange={e => setIncludeGeographyDescription(e.target.checked)} />
              </EuiFormRow>

              {includeGeographyDescription && (
                <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                  <EuiTextArea fullWidth value={campaignBuilder.geoSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, geoSummary: e.target.value })} />
                </EuiFormRow>
              )}
            </>
          ) : (
            <EuiFormRow fullWidth label='Geography Description'>
              <EuiTextArea fullWidth value={campaignBuilder.geoSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, geoSummary: e.target.value })} />
            </EuiFormRow>
          )}

          {campaignBuilder.isOffAdCritter ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeZipCodes' label='Zip Codes' checked={includeZipCodes} onChange={e => setIncludeZipCodes(e.target.checked)} />
              </EuiFormRow>

              {includeZipCodes && (
                <>
                  <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                    <PostalCodeFinder
                      onOptionClick={(zip: LocationPostalCode) =>
                        setCampaignBuilder({
                          ...campaignBuilder,
                          zips: [...(campaignBuilder.zips ?? []), zip]
                        })
                      }
                    />
                  </EuiFormRow>

                  <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                    <AcTargeterSelection
                      items={campaignBuilder.zips?.map(r => ({ key: r.id.toString(), label: r.name })) ?? []}
                      onItemRemoved={item => {
                        setCampaignBuilder({
                          ...campaignBuilder,
                          zips: campaignBuilder.zips?.filter(r => r.id.toString() !== item.key)
                        })
                      }}
                    />
                  </EuiFormRow>
                </>
              )}
            </>
          ) : (
            <>
              <EuiFormRow fullWidth label='Zip Codes'>
                <PostalCodeFinder
                  onOptionClick={(zip: LocationPostalCode) =>
                    setCampaignBuilder({
                      ...campaignBuilder,
                      zips: [...(campaignBuilder.zips ?? []), zip]
                    })
                  }
                />
              </EuiFormRow>

              <EuiFormRow fullWidth>
                <AcTargeterSelection
                  items={campaignBuilder.zips?.map(r => ({ key: r.id.toString(), label: r.name })) ?? []}
                  onItemRemoved={item => {
                    setCampaignBuilder({
                      ...campaignBuilder,
                      zips: campaignBuilder.zips?.filter(r => r.id.toString() !== item.key)
                    })
                  }}
                />
              </EuiFormRow>
            </>
          )}

          {isGoogleCampaign ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeKeywords' label='Keywords' checked={includeKeywords} onChange={e => setIncludeKeywords(e.target.checked)} />
              </EuiFormRow>

              {includeKeywords && (
                <>
                  <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                    <EuiTextArea fullWidth placeholder='Describe your keyword strategy...' value={campaignBuilder.keywordDescription || ''} onChange={e => setCampaignBuilder({ ...campaignBuilder, keywordDescription: e.target.value })} />
                  </EuiFormRow>

                  <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                    <EuiFlexGroup gutterSize='s'>
                      <EuiFlexItem>
                        <EuiFieldText
                          placeholder='Add keyword...'
                          value={keywordInput}
                          onChange={e => setKeywordInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && keywordInput.trim()) {
                              e.preventDefault()
                              setCampaignBuilder({ ...campaignBuilder, keywords: [...(campaignBuilder.keywords || []), keywordInput.trim()] })
                              setKeywordInput('')
                            }
                          }}
                        />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          size='s'
                          onClick={() => {
                            if (keywordInput.trim()) {
                              setCampaignBuilder({ ...campaignBuilder, keywords: [...(campaignBuilder.keywords || []), keywordInput.trim()] })
                              setKeywordInput('')
                            }
                          }}
                        >
                          Add
                        </EuiButton>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiFormRow>

                  {(campaignBuilder.keywords?.length ?? 0) > 0 && (
                    <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                      <EuiFlexGroup gutterSize='xs' wrap responsive={false}>
                        {campaignBuilder.keywords!.map((keyword, index) => (
                          <EuiFlexItem grow={false} key={index}>
                            <EuiBadge
                              color='hollow'
                              iconType='cross'
                              iconSide='right'
                              iconOnClick={() => setCampaignBuilder({ ...campaignBuilder, keywords: campaignBuilder.keywords?.filter((_, i) => i !== index) })}
                              iconOnClickAriaLabel={`Remove ${keyword}`}
                            >
                              {keyword}
                            </EuiBadge>
                          </EuiFlexItem>
                        ))}
                      </EuiFlexGroup>
                    </EuiFormRow>
                  )}
                </>
              )}
            </>
          ) : campaignBuilder.isOffAdCritter ? (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox id='includeAudienceDescription' label='Audience Description' checked={includeAudienceDescription} onChange={e => setIncludeAudienceDescription(e.target.checked)} />
              </EuiFormRow>

              {includeAudienceDescription && (
                <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                  <EuiTextArea fullWidth value={campaignBuilder.audienceSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, audienceSummary: e.target.value })} />
                </EuiFormRow>
              )}
            </>
          ) : (
            <EuiFormRow fullWidth label='Audience Description'>
              <EuiTextArea fullWidth value={campaignBuilder.audienceSummary} onChange={e => setCampaignBuilder({ ...campaignBuilder, audienceSummary: e.target.value })} />
            </EuiFormRow>
          )}

          {!campaignBuilder.isOffAdCritter && (campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.TV || campaignBuilder.campaignType === MasterCampaignPlanCampaignBuilderType.Audio) && (
            <EuiFormRow fullWidth label='Audience Segments'>
              <SegmentsForm
                campaignType={campaignBuilder.campaignType as unknown as CampaignType}
                segments={campaignBuilder.segmentTargets}
                onSegmentsChange={segmentTargets =>
                  setCampaignBuilder({
                    ...campaignBuilder,
                    segmentTargets
                  })
                }
              />
            </EuiFormRow>
          )}

          <EuiFormRow fullWidth label='Start Date *' isInvalid={moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3} error={['Start date must be at least 3 days from today']}>
            <EuiDatePicker minDate={moment().add(3, 'days')} selected={moment(campaignBuilder.startDate)} onChange={date => setCampaignBuilder({ ...campaignBuilder, startDate: date! })} />
          </EuiFormRow>

          <EuiFormRow fullWidth label='Duration *' isInvalid={campaignBuilder.intervalDuration <= 0} error={['Duration must be greater than 0']}>
            <EuiFieldNumber value={campaignBuilder.intervalDuration} append={`${campaignBuilder.intervalFrequency > 1 ? campaignBuilder.intervalFrequency : ''} ${campaignBuilder.intervalUnit}${campaignBuilder.intervalDuration > 1 ? 's' : ''}`} onChange={e => setCampaignBuilder({ ...campaignBuilder, intervalDuration: parseInt(e.target.value) })} />
          </EuiFormRow>

          <AcEuiFormRow fullWidth label='Budget *' isInvalid={budgetTouched && (!campaignBuilder.isOffAdCritter ? (!!campaignBuilder.intervalBudget && campaignBuilder.intervalBudget < 100) || !campaignBuilder.intervalBudget : !campaignBuilder.intervalBudget)} error={!campaignBuilder.isOffAdCritter ? ['Budget must be at least $100'] : []}>
            <EuiFieldNumber
              prepend={'$'}
              value={campaignBuilder.intervalBudget}
              min={!campaignBuilder.isOffAdCritter ? 100 : undefined}
              onClick={() => {
                setBudgetTouched(true)
              }}
              onChange={e =>
                setCampaignBuilder({
                  ...campaignBuilder,
                  intervalBudget: Number(e.target.value)
                })
              }
              append={`per ${campaignBuilder.intervalUnit}${campaignBuilder.intervalFrequency > 1 ? 's' : ''}`}
            />
          </AcEuiFormRow>

          {campaignBuilder.isOffAdCritter && !['Facebook', 'Google', 'Instagram', 'LinkedIn', 'TikTok'].includes(campaignBuilder.offAdCritterName || '') && (
            <>
              <EuiFormRow fullWidth>
                <EuiCheckbox
                  id='includeInProjections'
                  label='Include in Projections'
                  checked={includeInProjections}
                  onChange={e => {
                    const checked = e.target.checked
                    setIncludeInProjections(checked)
                    setCampaignBuilder({ ...campaignBuilder, includeInProjections: checked })
                  }}
                />
              </EuiFormRow>

              {includeInProjections && (
                <>
                  {isGoogleCampaign ? (
                    <AcEuiFormRow fullWidth label='CPC Estimate *' style={{ marginLeft: '2rem' }} isInvalid={!campaignBuilder.cpc || campaignBuilder.cpc <= 0}>
                      <EuiFieldNumber prepend='$' value={campaignBuilder.cpc} onChange={e => setCampaignBuilder({ ...campaignBuilder, cpc: Math.round(Number(e.target.value) * 100) / 100 })} step={0.01} />
                    </AcEuiFormRow>
                  ) : (
                    <AcEuiFormRow fullWidth label='CPM Estimate *' style={{ marginLeft: '2rem' }} isInvalid={!campaignBuilder.cpm || campaignBuilder.cpm <= 0}>
                      <EuiFieldNumber prepend='$' value={campaignBuilder.cpm} onChange={e => setCampaignBuilder({ ...campaignBuilder, cpm: Math.round(Number(e.target.value) * 100) / 100 })} step={0.01} />
                    </AcEuiFormRow>
                  )}

                  <AcEuiFormRow fullWidth label={`${isGoogleCampaign ? 'CPC' : 'CPM'} Margin of Error *`} style={{ marginLeft: '2rem' }} isInvalid={!campaignBuilder.marginOfError || campaignBuilder.marginOfError <= 0}>
                    <EuiFieldNumber append='%' value={campaignBuilder.marginOfError} onChange={e => setCampaignBuilder({ ...campaignBuilder, marginOfError: Number(e.target.value) })} />
                  </AcEuiFormRow>

                  <EuiFormRow fullWidth style={{ marginLeft: '2rem' }}>
                    <EuiCheckbox
                      id='includeInGeoHeatmap'
                      label='Add to Geo Distribution heatmap. (Requires zip codes above.)'
                      checked={includeInGeoHeatmap}
                      onChange={e => {
                        const checked = e.target.checked
                        setIncludeInGeoHeatmap(checked)
                        setCampaignBuilder({ ...campaignBuilder, includeInGeoHeatmap: checked })
                      }}
                      disabled={!includeZipCodes || (campaignBuilder.zips?.length ?? 0) === 0}
                    />
                  </EuiFormRow>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
